import type { AxiosRequestConfig } from 'axios';
import {
  TMemberCare,
  TMemberCareFilterOption,
  TMemberCareInvite,
  TMemberCareOnboarding,
  TMemberCareOrgAssignment,
  TMemberCareMember,
  TMemberCareContactDetailsWithoutEmail,
  TMemberCareOrganization,
  TMemberCareOrganizationDashboard,
  TMemberCareOrganizationRosterItem,
} from '@/types/MemberCare.types';
import { api, TCustomAxiosPaginationResponse, TCustomAxiosResponse } from '..';
import { getBeUserFieldNameFromFilterOption } from '@/utils/stringUtils';
import { MIN_CHARS_TO_DO_SEARCH, WELLNESS_DATA_MULTIPLIER } from '@/constants/numberVars';
import {
  MEMBER_CARE_SCORE_FILTER_FE_BE,
  MEMBER_CARE_SCORE_FILTER_VALUE_TO_LABEL,
  SexBeFe,
  SexFeBe,
} from '@/constants/stringVars';
import { TOrganization } from '@/types/Organization.types';
import { TFilterParams } from '@/types';
import { THealthDomainScores } from '@/types/Health.types';
import { TMemberCaresSortingColumn } from '@/routes/_authenticated/_admin/member-care';
import { TMemberCareOrganizationsSortingColumn } from '@/modules/memberCare/MemberCareOrganizationsTableDisplay';
import { TMemberCareMembersSortingColumn } from '@/modules/memberCare/MemberCareMembersTableDisplay';
import { apiPaths } from '@/services/api/paths';
import { SUPER_ADMIN_PAGE_SIZE } from '@/constants/numberVars';

export const getAllMemberCareSpecialists = async (
  page: number,
  filterOption: TMemberCareFilterOption,
  filterText: string,
  sortingColumn?: string,
  sortingDirection?: string,
): Promise<TCustomAxiosPaginationResponse<TMemberCare[]>> => {
  const filterTypeBE = filterOption ? getBeUserFieldNameFromFilterOption(filterOption) : '';

  const params: any = {
    limit: SUPER_ADMIN_PAGE_SIZE,
    page,
  };

  if (filterText && filterText?.length >= MIN_CHARS_TO_DO_SEARCH) {
    params[filterTypeBE] = filterText;
  }

  let sortBy = '';
  switch (sortingColumn as TMemberCaresSortingColumn) {
    case 'Email':
      sortBy = 'email';
      break;
    case 'Invitation Status':
      sortBy = 'invitation_status';
      break;
    case 'Last Sign In':
    default:
      sortBy = 'last_login';
      break;
  }

  params['sort_by'] = sortBy;
  params['order'] = sortingDirection;

  const { data } = await api.get(apiPaths.memberCare.users, {
    params,
  });

  return data;
};

export const getMemberCareSpecialistOwnProfile = async (
  config?: AxiosRequestConfig,
): Promise<TCustomAxiosResponse<TMemberCare>> => {
  const { data: response } = await api.get<TCustomAxiosResponse<TMemberCare>>(apiPaths.memberCare.ownProfile, config);

  const feData = { ...response.data, sex: SexBeFe[response.data.sex as keyof typeof SexBeFe] };

  return { ...response, data: feData };
};

export const getMemberCareSpecialistById = async (memberCareId: string): Promise<TCustomAxiosResponse<TMemberCare>> => {
  const { data: response } = await api.get<TCustomAxiosResponse<TMemberCare>>(
    apiPaths.memberCare.userById(memberCareId),
  );

  const feData = { ...response.data, sex: SexBeFe[response.data.sex as keyof typeof SexBeFe] };

  return { ...response, data: feData };
};

export const inviteMemberCareToPlatformPost = async (memberCareToInvite: TMemberCareInvite): Promise<string> => {
  const { data } = await api.post(apiPaths.memberCare.invite, memberCareToInvite);

  const result = data.data;

  return result;
};

export const assignOrgToMemberCarePost = async ({ memberCareId, orgId }: TMemberCareOrgAssignment): Promise<string> => {
  const { data } = await api.post(apiPaths.memberCare.assignOrganizations(memberCareId), {
    organization_ids: [orgId],
  });

  return data.data;
};

export const unassignOrgFromMemberCareDelete = async ({
  memberCareId,
  orgId,
}: TMemberCareOrgAssignment): Promise<string> => {
  const { data } = await api.delete(apiPaths.memberCare.unassignOrganizations(memberCareId), {
    data: {
      organization_ids: [orgId],
    },
  });

  return data.data;
};

export const updateMemberCarePut = async (memberCare: TMemberCareOnboarding, profilePhoto?: File): Promise<string> => {
  const sexBe = SexFeBe[memberCare.sex as keyof typeof SexFeBe];

  const formData = new FormData();
  formData.append('firstName', memberCare.firstName);
  formData.append('lastName', memberCare.lastName);
  formData.append('sex', sexBe);
  formData.append('phone', memberCare.phone);

  if (profilePhoto) {
    formData.append('profilePicture', new File([profilePhoto], profilePhoto.name));
  }
  if (memberCare.bio) {
    formData.append('bio', memberCare.bio);
  }
  if (memberCare.calendly_link) {
    formData.append('calendly_link', memberCare.calendly_link);
  }

  const { data } = await api.put(apiPaths.memberCare.updateProfile, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  const resultData = data.data;

  return resultData;
};

export const updateMemberCareProfilePhoto = async (
  memberCare: TMemberCare,
  newProfilePhoto: File,
): Promise<TMemberCare> => {
  const formData = new FormData();
  formData.append('firstName', memberCare.firstName);
  formData.append('lastName', memberCare.lastName);

  if (newProfilePhoto) {
    formData.append('profilePicture', new File([newProfilePhoto], newProfilePhoto.name));
  }

  const { data } = await api.put(apiPaths.memberCare.updateProfile, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  const resultData = data.data;

  return resultData;
};

export const updateMemberCareProfileBio = async (memberCare: TMemberCare, newBio: string): Promise<TMemberCare> => {
  const formData = new FormData();
  formData.append('firstName', memberCare.firstName);
  formData.append('lastName', memberCare.lastName);

  formData.append('bio', newBio);

  const { data } = await api.put(apiPaths.memberCare.updateProfile, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  const resultData = data.data;

  return resultData;
};

export const updateMemberCareProfileContactDetails = async (
  newMemberCareContactDetails: TMemberCareContactDetailsWithoutEmail,
): Promise<TMemberCare> => {
  const formData = new FormData();

  formData.append('firstName', newMemberCareContactDetails.firstName);
  formData.append('lastName', newMemberCareContactDetails.lastName);

  formData.append('phone', newMemberCareContactDetails.phone);
  formData.append('calendly_link', newMemberCareContactDetails.calendly_link);

  const { data } = await api.put(apiPaths.memberCare.updateProfile, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  const resultData = data.data;

  return resultData;
};

export const reactivateMemberCarePost = async (memberCareId: string): Promise<string> => {
  const { data } = await api.post(apiPaths.memberCare.activateUser(memberCareId));

  return data.data;
};

export const deactivateMemberCarePost = async (memberCareId: string): Promise<string> => {
  const { data } = await api.post(apiPaths.memberCare.deactivateUser(memberCareId));

  return data.data;
};

export const getMemberCareUnassignedOrganizations = async (
  page: number,
  memberCareId?: string,
): Promise<TCustomAxiosPaginationResponse<TOrganization[]>> => {
  const { data } = await api.get(apiPaths.memberCare.unassignedOrganizations(String(memberCareId)), {
    params: {
      limit: SUPER_ADMIN_PAGE_SIZE,
      page,
    },
  });

  return data;
};

export const getMemberCareMembers = async (
  page: number,
  filterParams?: TFilterParams,
  sortingColumn?: string,
  sortingDirection?: string,
): Promise<TCustomAxiosPaginationResponse<TMemberCareMember[]>> => {
  let sort_by = '';
  switch (sortingColumn as TMemberCareMembersSortingColumn) {
    case 'Last Activity':
      sort_by = 'assessmentSubmittedAt'; // TODO: possibly change once `assessmentSubmittedAt` is no longer the ONLY activity a user can do
      break;

    case 'Member Name':
      sort_by = 'firstName';
      break;

    case 'Organization':
      sort_by = 'organizationName';
      break;

    case 'Score':
    default:
      sort_by = 'totalScore';
      break;
  }

  const params: any = {
    page,
    sort_by,
    order: sortingDirection,
    fromDate: filterParams?.fromDate,
    toDate: filterParams?.toDate,
  };

  if (filterParams?.searchBy === 'Individual name') {
    params.search = filterParams?.searchText;
  }
  if (filterParams?.searchBy === 'Organization') {
    params.organizationName = filterParams?.searchText;
  }

  if (filterParams?.scoreFilter) {
    const scoreFilterBe =
      MEMBER_CARE_SCORE_FILTER_FE_BE[MEMBER_CARE_SCORE_FILTER_VALUE_TO_LABEL[filterParams.scoreFilter]];

    params['minScore'] = scoreFilterBe?.fromScore;
    params['maxScore'] = scoreFilterBe?.toScore;
  }

  const { data } = await api.get(apiPaths.memberCare.members, {
    params,
  });

  return data;
};

export const getMemberCareOrganizations = async (
  page: number,
  filterParams?: { searchText: string },
  sortingColumn?: string,
  sortingDirection?: string,
): Promise<TCustomAxiosPaginationResponse<TMemberCareOrganization[]>> => {
  let sortBy = '';
  switch (sortingColumn as TMemberCareOrganizationsSortingColumn) {
    case 'Organization Name':
      sortBy = 'name';
      break;

    case 'Members Count':
      sortBy = 'membersCount';
      break;

    case 'Submitted Documents Count':
      sortBy = 'submittedDocumentsCount';
      break;

    case 'Score':
    default:
      sortBy = 'score';
      break;
  }

  const params: any = {
    page,
    sortBy,
    order: sortingDirection,
  };

  params.search = filterParams?.searchText;

  const { data } = await api.get(apiPaths.memberCare.myOrganizations, {
    params,
  });

  return data;
};

export const getMemberCareOrganizationDashboard = async (
  organizationId: string,
): Promise<TCustomAxiosPaginationResponse<TMemberCareOrganizationDashboard>> => {
  const { data } = await api.get(apiPaths.memberCare.organizationDashboard(organizationId));

  const feData = data.data;

  const healthDomainScores = feData.score;

  for (const key in healthDomainScores) {
    if (typeof healthDomainScores[key as keyof THealthDomainScores] == 'number' && key !== 'totalScore') {
      healthDomainScores[key as keyof THealthDomainScores] *= WELLNESS_DATA_MULTIPLIER;
    }
  }

  feData.score = healthDomainScores;

  return { ...data, data: feData };
};

export const getMemberCareOrganizationRoster = async (
  page: number,
  filterParams?: { searchText: string; organizationId: string },
  // sortingColumn?: string,
  // sortingDirection?: string,
): Promise<TCustomAxiosPaginationResponse<TMemberCareOrganizationRosterItem[]>> => {
  const { data } = await api.get(apiPaths.memberCare.organizationRoster(String(filterParams?.organizationId)), {
    params: { page, search: filterParams?.searchText },
  });

  return data;
};

export const getMemberCareSpecialistsForOrganizations = async (
  page: number,
  org?: { orgId: string },
): Promise<TCustomAxiosPaginationResponse<TMemberCare[]>> => {
  const { data } = await api.get(apiPaths.memberCare.organizationMemberCares(String(org?.orgId)), {
    params: { page, limit: SUPER_ADMIN_PAGE_SIZE },
  });

  return data;
};

export const getMemberCareMemberById = async (memberId: string): Promise<TCustomAxiosResponse<TMemberCareMember>> => {
  const { data: response } = await api.get<TCustomAxiosResponse<TMemberCareMember>>(
    apiPaths.memberCare.userDetails(memberId),
  );

  const feData = { ...response.data };
  if (response.data?.sex) {
    feData.sex = SexBeFe[response.data?.sex as keyof typeof SexBeFe];
  }

  feData.totalScore = response.data.totalScore * WELLNESS_DATA_MULTIPLIER;

  const healthDomainScores = response.data.healthDomainScores;

  for (const key in healthDomainScores) {
    if (typeof healthDomainScores[key as keyof THealthDomainScores] == 'number') {
      healthDomainScores[key as keyof THealthDomainScores] *= WELLNESS_DATA_MULTIPLIER;
    }
  }

  feData.healthDomainScores = healthDomainScores;

  return { ...response, data: feData };
};
